import { JsonPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors } from '@angular/forms';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { TooltipModule } from 'primeng/tooltip';
import { TestModeService } from '../../../services/test-mode.service';

import { CesTooltipAutoDismissDirective } from '../../../directives/ces-tooltip-auto-dismiss.directive';
interface ControlSnapshot {
  name: string;
  value: unknown;
  status: string;
  dirty: boolean;
  touched: boolean;
  invalid: boolean;
  disabled: boolean;
  errors: ValidationErrors | null;
}

interface ErrorSnapshot {
  controlName: string;
  errorType: string;
  details: unknown;
}

@Component({
  selector: 'ces-form-debug-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CesTooltipAutoDismissDirective, JsonPipe, NgClass, PanelModule, TabViewModule, TableModule, TooltipModule],
  templateUrl: './ces-form-debug-panel.component.html',
  styleUrl: './ces-form-debug-panel.component.scss',
})
export class CesFormDebugPanelComponent {
  @Input() form?: FormGroup;
  @Input() title?: string;

  protected readonly t = inject(TestModeService);

  get formValue(): unknown {
    return (this.form?.value as unknown) ?? {};
  }

  get formLevelErrors(): ValidationErrors | null {
    return this.form?.errors ?? null;
  }

  get controlsList(): ControlSnapshot[] {
    if (!this.form) return [];

    const result: ControlSnapshot[] = [];
    this.processControls(this.form, '', result);
    return result;
  }

  get allErrors(): ErrorSnapshot[] {
    if (!this.form) return [];

    const errors: ErrorSnapshot[] = [];
    this.collectErrors(this.form, '', errors);
    return errors;
  }

  get formStructure(): string {
    if (!this.form) return '';
    return this.formatFormStructure(this.form);
  }

  private processControls(group: FormGroup, prefix: string, result: ControlSnapshot[]): void {
    Object.keys(group.controls).forEach(key => {
      const control = group.get(key);
      const controlName = prefix ? `${prefix}.${key}` : key;

      if (control instanceof FormGroup) {
        this.processControls(control, controlName, result);
      } else if (control) {
        result.push({
          name: controlName,
          value: control.value,
          status: control.status,
          dirty: control.dirty,
          touched: control.touched,
          invalid: control.invalid,
          disabled: control.disabled,
          errors: control.errors,
        });
      }
    });
  }

  private collectErrors(group: FormGroup, prefix: string, result: ErrorSnapshot[]): void {
    if (group.errors) {
      Object.keys(group.errors).forEach(errorKey => {
        result.push({
          controlName: prefix || 'formulaire',
          errorType: errorKey,
          details: group.errors?.[errorKey],
        });
      });
    }

    Object.keys(group.controls).forEach(key => {
      const control = group.get(key);
      const controlName = prefix ? `${prefix}.${key}` : key;

      if (control instanceof FormGroup) {
        this.collectErrors(control, controlName, result);
      } else if (control?.errors) {
        Object.keys(control.errors).forEach(errorKey => {
          result.push({
            controlName,
            errorType: errorKey,
            details: control.errors?.[errorKey],
          });
        });
      }
    });
  }

  private formatFormStructure(group: FormGroup, level = 0): string {
    const indent = '  '.repeat(level);
    let result = level === 0 ? 'FormGroup {\n' : '';

    Object.keys(group.controls).forEach(key => {
      const control = group.get(key);

      if (control instanceof FormGroup) {
        result += `${indent}  ${key}: FormGroup {\n`;
        result += this.formatFormStructure(control, level + 1);
        result += `${indent}  }\n`;
      } else if (control) {
        result += `${indent}  ${key}: FormControl { `;
        result += `status: ${control.status}, `;
        result += `validators: [${this.detectValidators(control)}] `;
        result += `}\n`;
      }
    });

    if (level === 0) {
      result += '}';
    }

    return result;
  }

  private detectValidators(control: AbstractControl): string {
    const validators: string[] = [];

    if (control.errors) {
      if (control.errors['required']) validators.push('required');
      if (control.errors['minlength']) validators.push('minLength');
      if (control.errors['maxlength']) validators.push('maxLength');
      if (control.errors['pattern']) validators.push('pattern');
      if (control.errors['email']) validators.push('email');
      if (control.errors['min']) validators.push('min');
      if (control.errors['max']) validators.push('max');

      Object.keys(control.errors).forEach(key => {
        if (!validators.includes(key)) {
          validators.push(key);
        }
      });
    }

    return validators.join(', ');
  }

  getDisplayValue(value: unknown): string {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  getValueTooltip(value: unknown): string {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'object') {
      const json = JSON.stringify(value, null, 2);
      return json.length > 5000 ? json.substring(0, 5000) + '...' : json;
    }
    return String(value);
  }
}
